import { TableColumn } from "../../components/Table";
import React, { useCallback, useEffect } from "react";
import JobConfigView from "../../components/JobConfigView";
import { JobType } from "../../constants/JobTypes";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  fetchJobConfigCausalLoop,
  fetchJobRunCausalLoop,
} from "../../redux/causalLoopSlice";
import Loader from "../../components/Loader";

const columns: TableColumn[] = [
  { key: "name", label: "Name" },
  {
    key: "createdDate",
    label: "Created Date",
    formatter: {
      type: "date",
    },
  },
  { key: "action", label: "Action" },
];

const causalLoopBreadCrumbs = [
  {
    name: "Home",
    url: "/",
  },
  {
    name: "Causal Loop - With Synthetic Control",
    url: "/causal-loop/landing",
  },
];

const CausalLoopList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { jobConfigList, jobRunList, loading } = useSelector(
    (state: RootState) => state.causalLoop
  );

  useEffect(() => {
    dispatch(fetchJobConfigCausalLoop());
    dispatch(fetchJobRunCausalLoop());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchJobConfigCausalLoop());
    dispatch(fetchJobRunCausalLoop());
  }, [dispatch]);

  // Like every job list page in this app, this list only fetches once on
  // mount - there's no live status push, so a job that finishes while this
  // page is open keeps showing "Running" until a manual refresh. Rather than
  // add polling to the shared JobConfigView (used by every job type), this
  // polls only here, and only while at least one Causal Loop job is actually
  // in flight, stopping once nothing is RUNNING/PENDING anymore.
  //
  // Only job RUN status can change while polling - job configs (name, date,
  // mapping) don't change once created, so the poll fetches jobRunList only,
  // not handleRefresh's full pair. fetchJobConfigCausalLoop.pending flips
  // `loading` true (see causalLoopSlice.ts), which swaps this whole page to
  // <Loader/> - calling it every 15s made the page flash to a full-screen
  // spinner and back for as long as a job was running.
  const hasInFlightJobs = jobRunList?.some(
    (run: any) => run.jobStatus === "RUNNING" || run.jobStatus === "PENDING"
  );
  useEffect(() => {
    if (!hasInFlightJobs) return;
    const interval = setInterval(() => dispatch(fetchJobRunCausalLoop()), 15000);
    return () => clearInterval(interval);
  }, [hasInFlightJobs, dispatch]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <JobConfigView
          jobConfigList={jobConfigList}
          jobRunList={jobRunList}
          breadCrumbs={causalLoopBreadCrumbs}
          jobType={JobType.CausalLoop}
          tableColumns={columns}
          onRefresh={handleRefresh}
        />
      )}
    </>
  );
};

export default CausalLoopList;
